import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import InvoiceUploader from "@/components/InvoiceUploader";
import { SentBackBanner, ResubmitFooter, hlFor, diffList, fileChange, useFileNames } from "@/components/EditResubmit";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function useProjectLines(pid) {
  const [heads, setHeads] = useState([]);
  useEffect(() => {
    setHeads([]);
    if (pid) api.get(`/projects/${pid}/expense-lines`).then(r => setHeads(r.data.heads || [])).catch(() => setHeads([]));
  }, [pid]);
  return heads;
}

export default function AdvanceEditForm({ adv, mode = "edit", onSaved }) {
  const editing = mode === "edit";
  const [projects, setProjects] = useState([]);
  const [f, setF] = useState({ project_id: adv.project_id || "", head_id: "", budget_line_id: adv.budget_line_id || "", amount: adv.amount || 0,
    purpose: adv.purpose || "", expected_settlement_date: (adv.expected_settlement_date || "").slice(0, 10), notes: adv.notes || "" });
  const [files, setFiles] = useState((adv.attachment_file_ids || []).map(id => ({ id, original_filename: "Current attachment" })));
  useFileNames(adv.attachment_file_ids, setFiles);
  const [busy, setBusy] = useState(false);
  const heads = useProjectLines(f.project_id);
  const orig = useRef(null);
  const hl = hlFor(editing ? adv.sent_back_fields : []);
  useEffect(() => { api.get("/v2/expenses/my-projects").then(r => setProjects(r.data)); }, []);
  useEffect(() => {
    if (!heads.length) return;
    const h = heads.find(x => (x.subtypes || []).some(s => s.id === f.budget_line_id));
    if (h && !f.head_id) setF(p => ({ ...p, head_id: h.id }));
    if (!orig.current && f.project_id === adv.project_id) {
      const s = h && h.subtypes.find(x => x.id === adv.budget_line_id);
      orig.current = { head: h?.code || "", sub: s?.code || "" };
    }
  }, [heads]);
  const subs = (heads.find(h => h.id === f.head_id) || {}).subtypes || [];
  const cur = { head: (heads.find(h => h.id === f.head_id) || {}).code, sub: (subs.find(s => s.id === f.budget_line_id) || {}).code };
  const pcode = (id) => (projects.find(p => p.id === id) || {}).project_code || "";
  const amt = parseFloat(f.amount || 0);
  const material = f.project_id !== adv.project_id || (f.budget_line_id || "") !== (adv.budget_line_id || "") || Math.abs(amt - (adv.amount || 0)) > 0.01;
  const changes = diffList([
    ["Project", pcode(adv.project_id), pcode(f.project_id)], ["Head", orig.current?.head, cur.head], ["Sub-type", orig.current?.sub, cur.sub],
    ["Amount", Number(adv.amount || 0).toFixed(2), amt.toFixed(2)], ["Purpose", adv.purpose, f.purpose],
    ["Expected settlement", (adv.expected_settlement_date || "").slice(0, 10), f.expected_settlement_date], ["Notes", adv.notes || "", f.notes],
  ], [fileChange(adv.attachment_file_ids, files.map(x => x.id))]);
  const body = () => ({ project_id: f.project_id, budget_line_id: f.budget_line_id || null, payee_id: adv.payee_id || null, purpose: f.purpose,
    amount: amt, expected_settlement_date: f.expected_settlement_date || null, notes: f.notes, attachment_file_ids: files.map(x => x.id) });
  const save = async (action, response) => {
    setBusy(true);
    try {
      if (editing) await api.put(`/advances/${adv.id}/sent-back-edit`, { ...body(), action, response_to_reviewer: response });
      else await api.post("/advances", body());
      toast.success(editing ? (action === "draft" ? "Draft saved" : "Resubmitted") : "New advance requested");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <DialogHeader><DialogTitle>{editing ? `Edit & resubmit · ${adv.advance_id}` : `New advance · copied from ${adv.advance_id}`}</DialogTitle></DialogHeader>
      {editing ? <SentBackBanner doc={adv} /> : <p className="text-xs text-slate-600 rounded bg-slate-50 border p-2" data-testid="adv-copy-note">Pre-filled from rejected {adv.advance_id}. Submitting creates a new advance with a new reference.</p>}
      <div className="space-y-3" data-testid="adv-edit-form">
        <div className={hl("project")}><Label>Project *</Label>
          <Select value={f.project_id} onValueChange={(v) => setF({ ...f, project_id: v, head_id: "", budget_line_id: "" })}>
            <SelectTrigger data-testid="adv-edit-project"><SelectValue placeholder="Select project" /></SelectTrigger>
            <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.project_code} · {p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className={`grid grid-cols-2 gap-3 ${hl("budget_line")}`}>
          <div><Label>Head</Label>
            <Select value={f.head_id} onValueChange={(v) => setF({ ...f, head_id: v, budget_line_id: "" })} disabled={!heads.length}>
              <SelectTrigger data-testid="adv-edit-head"><SelectValue placeholder="Select head" /></SelectTrigger>
              <SelectContent>{heads.map(h => <SelectItem key={h.id} value={h.id}>{h.code} · {h.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Sub-type</Label>
            <Select value={f.budget_line_id} onValueChange={(v) => setF({ ...f, budget_line_id: v })} disabled={!f.head_id}>
              <SelectTrigger data-testid="adv-edit-subtype"><SelectValue placeholder="Select sub-type" /></SelectTrigger>
              <SelectContent>{subs.map(s => <SelectItem key={s.id} value={s.id} data-testid={`adv-edit-sub-${s.code}`}>{s.code} · {s.name} <span className="text-xs text-emerald-600 ml-1">Remaining {inr(s.remaining)}</span></SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className={hl("amount")}><Label>Amount *</Label><Input type="number" step="0.01" value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} data-testid="adv-edit-amount" /></div>
          <div className={hl("expected_settlement_date")}><Label>Expected settlement</Label><Input type="date" value={f.expected_settlement_date} onChange={e => setF({ ...f, expected_settlement_date: e.target.value })} data-testid="adv-edit-date" /></div>
        </div>
        <div className={hl("purpose")}><Label>Purpose *</Label><Textarea rows={2} value={f.purpose} onChange={e => setF({ ...f, purpose: e.target.value })} data-testid="adv-edit-purpose" /></div>
        <div className={hl("notes")}><Label>Notes</Label><Textarea rows={2} value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} data-testid="adv-edit-notes" /></div>
        <div className={hl("attachments")}><InvoiceUploader label="Supporting documents (estimate / quotation)" files={files} onChange={setFiles} testId="adv-edit-files" /></div>
        {editing
          ? <ResubmitFooter kind="advance" doc={adv} changes={changes} material={material} busy={busy} onSave={save} onWithdrawn={onSaved}
              blockReason={amt <= 0 ? "Amount must be positive" : !f.purpose.trim() ? "Purpose is required" : !f.project_id ? "Pick a project" : null} />
          : <DialogFooter><Button disabled={busy || amt <= 0 || !f.purpose.trim() || !f.project_id} onClick={() => save("create")} data-testid="adv-copy-submit">{busy ? "Saving…" : "Submit new advance"}</Button></DialogFooter>}
      </div>
    </>
  );
}
